import { Avatar, Text, cx, identityTone } from '../design/index.js';
import type { Person } from '../api.js';
import styles from './PersonSelector.module.css';

export interface PersonSelectorProps {
  people: Person[];
  selectedUserId: string;
  onSelect: (person: Person) => void;
  className?: string;
}

/**
 * One avatar-and-name control per dashboard the caller can view: themself,
 * then anyone who shares their own dashboard with them, in whichever order
 * `GET /api/people` already returns ("me first"). A colour is never chosen
 * here — `identityTone(userId)` derives it from the id, the same rule the
 * header's own avatar uses, so a person reads as the same colour everywhere.
 *
 * Renders nothing at all when there is only one person: a selector holding
 * just yourself is noise on a dashboard nobody shares with, not a state worth
 * a control for.
 */
export function PersonSelector({ people, selectedUserId, onSelect, className }: PersonSelectorProps) {
  if (people.length <= 1) return null;

  return (
    <div
      className={cx(styles.list, className)}
      role="group"
      aria-label="Whose dashboard to show"
      data-testid="person-selector"
    >
      {people.map((person) => {
        const isSelected = person.userId === selectedUserId;
        const firstName = person.displayName.split(' ')[0] ?? person.displayName;
        return (
          <button
            key={person.userId}
            type="button"
            aria-current={isSelected ? 'true' : undefined}
            className={cx(styles.person, isSelected && styles.personSelected)}
            data-testid={`person-selector-${person.userId}`}
            onClick={() => onSelect(person)}
          >
            <Avatar
              size="sm"
              tone={identityTone(person.userId)}
              src={person.avatarUrl ?? undefined}
              initials={firstName.slice(0, 1)}
              alt=""
            />
            <Text size="sm" weight={isSelected ? 'medium' : 'regular'} className={styles.name}>
              {person.isSelf ? 'You' : firstName}
            </Text>
          </button>
        );
      })}
    </div>
  );
}
